<template>
  <el-dialog :visible.sync="dialogVisible" :show-close="false" width="510px"
    custom-class="cancellation-dialog" @close="closeDialog">
    <button type="button" class="cancellation-close" @click="closeDialog">×</button>

    <div v-if="cancellationStep === 1" class="cancellation-content">
      <div class="cancellation-hero">😔</div>
      <h2>¿Por qué quieres cancelar?</h2>
      <p class="cancellation-subtitle">Tu respuesta nos ayuda a mejorar Samii para todos los talleres.</p>

      <div class="cancellation-reasons">
        <div v-for="reason in cancellationReasons" :key="reason.id"
          class="cancellation-reason" :class="{ selected: selectedCancellationReason && selectedCancellationReason.id === reason.id }"
          role="button" tabindex="0" @click="selectCancellationReason(reason)"
          @keydown.enter.prevent="selectCancellationReason(reason)"
          @keydown.space.prevent="selectCancellationReason(reason)">
          <div class="cancellation-reason-main">
            <span class="cancellation-emoji">{{ reason.emoji || '😔' }}</span>
            <div class="cancellation-reason-copy">
              <strong>{{ reason.title }}</strong>
              <span>{{ reason.subtitle }}</span>
            </div>
            <span class="cancellation-radio"><span v-if="selectedCancellationReason && selectedCancellationReason.id === reason.id"></span></span>
          </div>
          <textarea v-if="selectedCancellationReason && selectedCancellationReason.id === reason.id"
            v-model="cancellationDetails" class="cancellation-details" rows="3" @click.stop
            placeholder="Cuéntanos más..."></textarea>
        </div>
      </div>

      <el-button class="cancellation-primary-button" :disabled="!selectedCancellationReason"
        @click="cancellationStep = 2">Continuar →</el-button>
    </div>

    <div v-else class="cancellation-content">
      <div class="cancellation-warning">⚠</div>
      <h2>¿Confirmas que quieres cancelar?</h2>
      <p class="cancellation-subtitle">Tu cuenta se cancelará al final del período de facturación actual.</p>

      <div class="cancellation-losses">
        <span>Vas a perder:</span>
        <p>× &nbsp; Acceso a todas tus órdenes activas e historial</p>
        <p>× &nbsp; Tus clientes y técnicos registrados</p>
        <p>× &nbsp; Reportes y estadísticas de tu taller</p>
        <p>× &nbsp; El precio actual (si cancelas y regresas, los precios pueden cambiar)</p>
      </div>

      <label class="cancellation-label">¿Algo más que quieras decirnos? (opcional)</label>
      <textarea v-model="finalCancellationDetails" class="cancellation-details confirmation-details" rows="3"
        placeholder="Tu opinión importa para los próximos talleres..."></textarea>

      <el-button class="cancellation-primary-button" :loading="cancellationSubmitting" @click="submitCancellation">
        Sí, cancelar mi suscripción
      </el-button>
      <el-button class="cancellation-back-button" :disabled="cancellationSubmitting" @click="closeDialog">
        No, quiero quedarme
      </el-button>
      <p class="cancellation-footnote">La cancelación es efectiva al final de tu período actual. No hay cobros adicionales.</p>
    </div>
  </el-dialog>
</template>

<script>
import error from "@/mixins/error";
import PaymentService from "@/services/PaymentService";

export default {
  name: "MembershipCancellation",
  mixins: [error],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      cancellationStep: 1,
      cancellationReasons: [],
      selectedCancellationReason: null,
      cancellationDetails: "",
      finalCancellationDetails: "",
      cancellationSubmitting: false,
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
  },
  watch: {
    value(value) {
      if (value) this.openDialog();
    },
  },
  methods: {
    async openDialog() {
      this.resetDialog();
      try {
        this.cancellationReasons = await PaymentService.cancellationReasons();
      } catch (e) {
        this.closeDialog();
        this.$toastr.e(this.getErrorMessage(e));
      }
    },
    closeDialog() {
      this.dialogVisible = false;
      this.resetDialog();
    },
    resetDialog() {
      this.cancellationStep = 1;
      this.cancellationReasons = [];
      this.selectedCancellationReason = null;
      this.cancellationDetails = "";
      this.finalCancellationDetails = "";
    },
    selectCancellationReason(reason) {
      this.selectedCancellationReason = reason;
    },
    async submitCancellation() {
      if (!this.selectedCancellationReason || this.cancellationSubmitting) return;
      try {
        this.cancellationSubmitting = true;
        await PaymentService.cancelAccount({
          cancellationReasonId: this.selectedCancellationReason.id,
          details: [this.cancellationDetails.trim(), this.finalCancellationDetails.trim()].filter(Boolean).join("\n\n") || "Canceló sin responder.",
          additionalOpinion: this.finalCancellationDetails.trim(),
        });
        this.closeDialog();
        await this.$swal.fire({
          title: "Solicitud registrada",
          text: "Tu suscripción se cancelará al final del período de facturación actual.",
          icon: "success",
          confirmButtonText: "Aceptar",
        });
      } catch (e) {
        this.$toastr.e(this.getErrorMessage(e));
      } finally {
        this.cancellationSubmitting = false;
      }
    },
  },
};
</script>

<style>
.cancellation-dialog { border-radius: 16px; }
.cancellation-dialog .el-dialog__header,
.cancellation-dialog .el-dialog__footer { display: none; }
.cancellation-dialog .el-dialog__body { padding: 24px 20px 22px; }
.cancellation-content { position: relative; text-align: center; color: #172033; }
.cancellation-close { position: absolute; top: 8px; right: 8px; border: 0; background: transparent; color: #718096; font-size: 26px; cursor: pointer; line-height: 1; padding: 6px 8px; z-index: 1; }
.cancellation-hero { font-size: 35px; margin-bottom: 6px; }
.cancellation-warning { display: inline-flex; align-items: center; justify-content: center; width: 48px; height: 48px; border-radius: 50%; background: #fff0f0; color: #ef4444; font-size: 28px; margin-bottom: 10px; }
.cancellation-content h2 { margin: 0 0 8px; font-size: 21px; font-weight: 700; }
.cancellation-subtitle { margin: 0 0 24px; color: #718096; font-size: 14px; }
.cancellation-reasons { display: flex; flex-direction: column; gap: 8px; text-align: left; }
.cancellation-reason { border: 1px solid #dce4ef; border-radius: 12px; padding: 12px 14px; cursor: pointer; transition: border-color .15s, box-shadow .15s; }
.cancellation-reason, .cancellation-reason * { cursor: pointer; }
.cancellation-reason textarea { cursor: text; }
.cancellation-reason.selected { border: 2px solid #8b2b96; padding: 11px 13px; background: #fcf8fd; box-shadow: 0 3px 10px rgba(139, 43, 150, .12); }
.cancellation-reason-main { display: flex; align-items: center; gap: 12px; }
.cancellation-emoji { width: 24px; text-align: center; font-size: 20px; }
.cancellation-reason-copy { display: flex; flex: 1; min-width: 0; flex-direction: column; gap: 3px; }
.cancellation-reason-copy strong { font-size: 14px; font-weight: 700; }
.cancellation-reason-copy span { color: #718096; font-size: 12px; }
.cancellation-radio { width: 16px; height: 16px; border: 2px solid #cbd5e1; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.cancellation-reason.selected .cancellation-radio { border-color: #8b2b96; }
.cancellation-radio span { width: 8px; height: 8px; border-radius: 50%; background: #8b2b96; }
.cancellation-details { display: block; width: 100%; box-sizing: border-box; margin-top: 10px; border: 1px solid #dce4ef; border-radius: 8px; padding: 10px 11px; resize: vertical; color: #172033; font-family: inherit; font-size: 13px; outline: none; }
.cancellation-details:focus { border-color: #8b2b96; }
.cancellation-primary-button { width: 100%; margin-top: 20px; border: 0 !important; border-radius: 11px !important; background: #8b2b96 !important; color: white !important; font-weight: 700 !important; height: 44px; }
.cancellation-primary-button.is-disabled { background: #c9a8cf !important; color: white !important; }
.cancellation-losses { text-align: left; background: #fff4f4; border-radius: 12px; padding: 14px 16px; margin: 20px 0 16px; color: #475569; font-size: 12px; }
.cancellation-losses span { display: block; margin-bottom: 6px; color: #334155; font-weight: 700; }
.cancellation-losses p { margin: 2px 0; line-height: 1.35; }
.cancellation-losses p::first-letter { color: #ef4444; }
.cancellation-label { display: block; text-align: left; color: #334155; font-size: 12px; margin-bottom: 6px; }
.confirmation-details { margin-top: 0; text-align: left; }
.cancellation-back-button { width: 100%; margin: 8px 0 0 !important; border: 1px solid #ef4444 !important; border-radius: 11px !important; color: #ef4444 !important; height: 44px; font-weight: 700 !important; }
.cancellation-footnote { color: #718096; font-size: 11px; margin: 12px 0 0; }
</style>
